import { useState, useEffect, useReducer, Fragment } from 'react';
import CardTemplate from 'components/ui-templates/CardTemplate';
import Button from 'components/ui-components/Button';
import TextInput from 'components/ui-components/TextInput';
import { gaFailedAccountActivation } from 'data_layer/dataLayerRegisterForm';
import { useHTTP } from 'hooks/useRequest';
import LoadingSpinner from 'components/ui-components/loaders/LoadingSpinner';
import { initialFormFields, reducer, actionTypes } from './stateManager';

const ActivateAccount = ({
  cardTitle,
  cardDescription,
  cardButtonText,
  userKey,
  customer: { id: userID, title, forename, surname, email, telephone },
}) => {
  const [formFields, dispatch] = useReducer(reducer, initialFormFields);

  const [createUser, setCreateUser] = useState(false);

  const configUrl = `/mycef/activations/${userID}`;

  const requestConfig = {
    url: createUser ? configUrl : null,
    method: 'put',
    headers: {
      Accept: 'application/json',
    },
    data: {
      key: userKey,
      customer: {
        title,
        forename,
        surname,
        email,
        telephone,
        username: formFields[0].value,
        password: formFields[1].value,
        password_confirmation: formFields[2].value,
      },
    },
  };

  const { data, error, loading } = useHTTP(requestConfig);

  const { UPDATE_AND_VALIDATE_FIELD, VALIDATE_ALL_FIELDS, USERNAME_TAKEN } =
    actionTypes;

  // Update and validate a single field
  const handleInput = (value, index) => {
    dispatch({
      type: UPDATE_AND_VALIDATE_FIELD,
      payload: { value, index },
    });
  };

  // Validate all fields at once and submit form / fire GA Failed event
  const submitDetails = e => {
    e.preventDefault();

    const username = formFields[0].value;
    const password = formFields[1].value;
    const confirmPassword = formFields[2].value;

    const formIsValid = formFields.every(field => !field.error);

    if (formIsValid) dispatch({ type: VALIDATE_ALL_FIELDS });

    if (username && password && confirmPassword && formIsValid) {
      setCreateUser(true);
    } else {
      gaFailedAccountActivation();
    }
  };

  useEffect(() => {
    /** redirect user to homepage with new modal
     */
    if (data?.status === 200) {
      window.dataLayer.push({
        event: cardTitle ? 'registerAccount' : 'createAccount',
      });
      window.location.href = `${window.location.origin}?first_login=true${
        cardTitle && '&existing=true'
      }`;
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      const usernameError = Object.entries(error.error.data.error);
      dispatch({
        type: USERNAME_TAKEN,
        payload: usernameError.flat().join(' '),
      });
      setCreateUser(false);
    }
  }, [error]);

  const passwordRequirements = [
    'Must be 12 - 256 characters long',
    'Cannot have spaces at the start or end',
  ];

  const submitText = cardButtonText || 'Activate Account';

  return (
    <CardTemplate
      icon="success"
      title={cardTitle || 'Activate Your Account'}
      description={
        cardDescription ||
        'Please create a username and password to complete your account setup'
      }>
      <h2 className="ff-body fs-20 lh-1 tc-main mb-8">
        Create Username and Password
      </h2>
      <form className="mb-0" onSubmit={submitDetails}>
        {formFields.map(
          (
            { label, description, id, value, type, className, error: err },
            index,
          ) => (
            <Fragment key={label}>
              <TextInput
                label={label}
                description={description}
                id={id}
                type={type}
                value={value}
                onChange={e => handleInput(e.target.value, index)}
                className={className}
                errors={err || null}
              />
              {err && <p className="fs-12 ff-body tc-cef-red mt-6">{err}</p>}
              {label === 'Username' && (
                <p className="fs-12 tc-secondary fw-400 mt-5">
                  You can use letters, numbers and .-_@,
                </p>
              )}
              {label === 'Password' && (
                <div className="activation-modal__password-requirements-box mt-12 ptb-12 plr-16 fs-13">
                  <p className="tc-main fw-500 mb-3">Password Requirements</p>
                  <ul className="tc-secondary mb-0 ml-12">
                    {passwordRequirements.map(requirement => (
                      <li key={requirement} className="ls-square">
                        {requirement.includes('special characters:') ? (
                          <>
                            {requirement.split(':')[0]}: <br />
                            {requirement.split(':')[1]}
                          </>
                        ) : (
                          requirement
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Fragment>
          ),
        )}

        <Button
          className="cef-btn cef-btn--feature cef-btn--xxl cef-btn--green mt-32"
          type="submit"
          disabled={loading}>
          {loading ? <LoadingSpinner /> : submitText}
        </Button>
      </form>
    </CardTemplate>
  );
};

export default ActivateAccount;
